import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Button, Input } from '../../../components';
import { PasswordChangeData, UserProfileForm } from '../types';

interface GeneralSettingsTabProps {
  profile: UserProfileForm;
  onUpdateProfile: (updates: Partial<UserProfileForm>) => void;
  onChangePassword: (data: PasswordChangeData) => Promise<{ success: boolean; error?: string }>;
  isSaving: boolean;
}

export const GeneralSettingsTab: React.FC<GeneralSettingsTabProps> = ({
  profile: _profile,
  onUpdateProfile: _onUpdateProfile,
  onChangePassword,
  isSaving,
}) => {
  const { t } = useTranslation();
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    old_password: '',
    new_password1: '',
    new_password2: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handlePasswordChange = async () => {
    setPasswordError(null);

    if (!passwordData.old_password || !passwordData.new_password1 || !passwordData.new_password2) {
      setPasswordError(t('profile.pleaseFillAllPasswordFields'));
      return;
    }

    if (passwordData.new_password1 !== passwordData.new_password2) {
      setPasswordError(t('profile.newPasswordsDoNotMatch'));
      return;
    }

    if (passwordData.new_password1.length < 8) {
      setPasswordError(t('profile.passwordMustBeAtLeast8Characters'));
      return;
    }

    const result = await onChangePassword(passwordData);
    if (result.success) {
      setPasswordData({ old_password: '', new_password1: '', new_password2: '' });
    } else {
      setPasswordError(result.error || t('profile.failedToChangePassword'));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.generalSettings')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('profile.password')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="currentPassword"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('profile.currentPassword')}
                  </label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordData.old_password}
                    onChange={e =>
                      setPasswordData(prev => ({ ...prev, old_password: e.target.value }))
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('profile.newPassword')}
                  </label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordData.new_password1}
                    onChange={e =>
                      setPasswordData(prev => ({ ...prev, new_password1: e.target.value }))
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('profile.confirmNewPassword')}
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.new_password2}
                    onChange={e =>
                      setPasswordData(prev => ({ ...prev, new_password2: e.target.value }))
                    }
                    className="w-full"
                  />
                </div>
                {passwordError && <div className="text-red-600 text-sm">{passwordError}</div>}
                <Button
                  onClick={handlePasswordChange}
                  disabled={isSaving}
                  variant="gradient-multi"
                  className="w-full"
                >
                  <span className="material-icons mr-2">lock</span>
                  {t('profile.changePassword')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
